import { inject, injectable } from "inversify";
import { ScenarioElement } from "../../../model/playerConfiguration/ScenarioElement";
import { QualityLevel } from "../../../model/qualityLevel/QualityLevel";
import { ScenarioPath } from "../../../model/scenarioPath/ScenarioPath";
import { TYPES } from "../../di/types";
import { VideoLayer } from "../../layer/VideoLayer";
import { VideoLayerObserver } from "../../layerObserver/VideoLayerObserver";

export interface VideoLayerManager {
  newScenarioElement(scenarioPath: ScenarioPath): void;
  // TODO THIS SHOULD BE TWO SEPARATE METHODS
  loadVideoAndGetDuration(progress: number): Promise<number>;
  reloadVideo(): void;
  getCurrentScenarioElement(): ScenarioElement;
  getCurrentSeekTime(): number;
  getCurrentFrameTime(): number;
  getBufferedTime(): number;
  getTotalTime(): number;
  getVolume(): number;
  getIsPlaying(): boolean;
  getIsFullyLoaded(): boolean;
  getIsWaiting(): boolean;
  play(): void;
  pause(): void;
  moveTo(time: number): void;
  moveBy(time: number): void;
  reloadFrame(): void;
  changeVolume(volume: number): void;
  changeMuted(isMuted: boolean): void;
  getBandwidth(): number;
  getQualityLevels(): QualityLevel[];
  getCurrentQualityLevel(): QualityLevel;
  activateSubtitles(source: string): void;
  registerObserver(observer: VideoLayerObserver): void;
  initializeLayer(): void;
  hide(): void;
}

@injectable()
export class VideoLayerManagerImpl implements VideoLayerManager {
  private scenarioElement: ScenarioElement;

  constructor(@inject(TYPES.layer.VideoLayer) private layer: VideoLayer) {}

  newScenarioElement(scenarioPath: ScenarioPath): void {
    this.scenarioElement = scenarioPath.currentElement;
  }

  loadVideoAndGetDuration(progress: number): Promise<number> {
    return this.layer
      .loadVideo(this.scenarioElement.video.sources, this.scenarioElement.video.subtitles, progress)
      .then(() => this.layer.getTotalTime());
  }

  reloadVideo() {
    this.layer.reloadVideo();
  }

  getCurrentScenarioElement(): ScenarioElement {
    return this.scenarioElement;
  }

  getCurrentSeekTime(): number {
    return this.layer.getCurrentSeekTime();
  }

  getCurrentFrameTime(): number {
    return this.layer.getCurrentFrameTime();
  }

  getBufferedTime(): number {
    return this.layer.getBufferedTime();
  }

  getTotalTime(): number {
    return this.layer.getTotalTime();
  }

  getVolume(): number {
    return this.layer.getVolume();
  }

  getIsPlaying(): boolean {
    return this.layer.getIsPlaying();
  }

  getIsFullyLoaded(): boolean {
    return this.layer.getIsFullyLoaded();
  }

  getIsWaiting(): boolean {
    return this.layer.getIsWaiting();
  }

  play(): void {
    this.layer.play();
  }

  pause(): void {
    this.layer.pause();
  }

  moveTo(time: number): void {
    this.layer.moveTo(time);
  }

  moveBy(time: number): void {
    this.layer.moveTo(this.layer.getCurrentSeekTime() + time);
  }

  reloadFrame(): void {
    this.layer.reloadFrame();
  }

  changeVolume(volume: number): void {
    this.layer.changeVolume(volume);
  }

  changeMuted(isMuted: boolean): void {
    this.layer.changeMuted(isMuted);
  }

  getBandwidth(): number {
    return this.layer.getBandwidth();
  }

  getQualityLevels(): QualityLevel[] {
    return this.layer.getQualityLevels();
  }

  getCurrentQualityLevel(): QualityLevel {
    return this.layer.getCurrentQualityLevel();
  }

  activateSubtitles(source: string): void {
    this.layer.activateSubtitles(source);
  }

  registerObserver(observer: VideoLayerObserver): void {
    this.layer.registerObserver(observer);
  }

  initializeLayer(): void {
    this.layer.initializeLayer();
  }

  hide(): void {
    this.layer.hide();
  }
}
